import { CLIPPER_X, CLIPPER_WIDTH, CLIPPER_DUCK_HEIGHT, GROUND_Y, type ClipperRunState } from '@/lib/loading-game/clipperRun';

let pending: Promise<HTMLImageElement | null> | undefined;

/** Decode once for every mounted processing game; the canvas retains its drawing fallback. */
export function loadClipperSprites(): Promise<HTMLImageElement | null> {
  if (pending) return pending;
  pending = new Promise((resolve) => {
    const image = new Image();
    const finish = (value: HTMLImageElement | null) => {
      clearTimeout(timer);
      image.onload = null;
      image.onerror = null;
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), 10_000);
    image.onload = () => finish(image.naturalWidth === 1536 && image.naturalHeight === 1024 ? image : null);
    image.onerror = () => finish(null);
    image.src = '/images/loading-game/clipper-sprites-v3.webp';
  });
  return pending;
}

export function clipperSpriteIndex(state: ClipperRunState): number {
  if (state.phase === 'dead') return 5;
  if (state.feetY < GROUND_Y - 0.5) return 3;
  if (state.ducking) return 4;
  if (state.phase === 'ready') return 0;
  return 1 + Math.floor(state.distance / 32) % 2;
}

// Crop each silhouette rather than assuming the generated poses stop at cell
// boundaries. Anchors are the torso center and shoe contact point in atlas pixels.
const SPRITE_FRAMES = [
  { crop: [32, 64, 464, 416], bodyX: 268, groundY: 473 },
  { crop: [560, 72, 424, 404], bodyX: 776, groundY: 469 },
  { crop: [1080, 68, 428, 408], bodyX: 1291, groundY: 471 },
  { crop: [52, 544, 476, 392], bodyX: 266, groundY: 930 },
  { crop: [580, 624, 424, 328], bodyX: 785, groundY: 947 },
  { crop: [1070, 636, 408, 324], bodyX: 1296, groundY: 953 },
] as const;
const SPRITE_SCALE = 64 / 512;

export function drawClipperSprite(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  state: ClipperRunState,
): void {
  const index = clipperSpriteIndex(state);
  const { crop: [x, y, width, height], bodyX, groundY } = SPRITE_FRAMES[index];
  const scaleY = index === 4 ? CLIPPER_DUCK_HEIGHT / (groundY - y) : SPRITE_SCALE;
  ctx.drawImage(image, x, y, width, height,
    CLIPPER_X + CLIPPER_WIDTH / 2 - (bodyX - x) * SPRITE_SCALE,
    state.feetY - (groundY - y) * scaleY,
    width * SPRITE_SCALE, height * scaleY);
}
